import { cn } from '@low6-pickem/ui';
import { getTeamCode } from '@/utils/teamCodes';
import { useCachedImage } from '../../hooks/useCachedImage';
import { useTenant } from '@/contexts/TenantContext';
import AdSlot from '../ads/AdSlot';
import { generateAdDivId } from '@/config/ads.config';
import { useOverLogoPlacement } from '@/hooks/useOverLogoPlacement';
import {
  isNbaSquaresSport,
  squaresTeamAssetPadding,
  RESULTS_MODAL_HERO_OUTER_H_PX,
  RESULTS_MODAL_HERO_OUTER_W_PX,
  RESULTS_MODAL_HERO_SQUARE_FIT_PX,
  RESULTS_MODAL_THUMB_SQUARE_FIT_PX,
} from '@/utils/squaresHeaderAssets';
import {
  SQUARES_MODAL_CARD_GRADIENT,
  SQUARES_MODAL_CONTAINER_CLASS,
  SQUARES_MODAL_OVERLAY_CLASS,
  SQUARES_MODAL_SCROLL_CONTAINER_CLASS,
  SquaresModalBackdrop,
  SquaresModalCloseButton,
} from './SquaresModalShell';

function formatResultsQuarterHeader(quarter: string, isNba: boolean): string {
  const u = String(quarter || '')
    .trim()
    .toUpperCase();
  if (u === 'FINAL') return 'FT';
  if (isNba && (u === 'Q2' || u === '2Q')) return 'HT';
  return quarter;
}

function formatResultsQuarterSubLabel(quarter: string, isNba: boolean): string {
  const u = String(quarter || '')
    .trim()
    .toUpperCase();
  if (isNba && (u === 'Q2' || u === '2Q')) return 'HT';
  if (u === 'FT' || u === 'FINAL' || (isNba && (u === 'Q4' || u === '4Q'))) return 'FT';
  return quarter;
}

export interface PotentialWinning {
  /** Quarter label (e.g., "1Q", "2Q", "3Q", "FT") */
  quarter: string;
  /** Dollar amount (e.g., "$2.67") */
  amount: string;
  /** Graded score for team 1 (row team) at end of this period */
  team1Score?: number;
  /** Graded score for team 2 (col team) at end of this period */
  team2Score?: number;
}

export interface SquareResultsModalProps {
  /** Is the modal open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Team 1 name/abbreviation */
  team1Name?: string;
  /** Team 1 jersey image URL */
  team1JerseyUrl?: string;
  /** Team 2 name/abbreviation */
  team2Name?: string;
  /** Team 2 jersey image URL */
  team2JerseyUrl?: string;
  /** Team 1 score */
  team1Score?: number;
  /** Team 2 score */
  team2Score?: number;
  /** Sponsor logo URL (e.g., Wrangler SQUARES logo) */
  sponsorLogoUrl?: string;
  /** Number of users who selected this square */
  userCount?: number;
  /** Percentage of users who selected this square (0-100) */
  userPercentage?: number;
  /** Total number of users in the contest */
  totalUsers?: number;
  /** Potential winnings for each quarter */
  potentialWinnings?: PotentialWinning[];
  /** Is the game completed? If true, shows "WINNINGS" instead of "POTENTIAL WINNINGS" */
  isCompleted?: boolean;
  /** User's square row digit (0-9) */
  userSquareRowDigit?: number;
  /** User's square column digit (0-9) */
  userSquareColDigit?: number;
  /** Contest sport (e.g. NBA, NCAAMB) — NBA jerseys framed like NCAA; Q2 shown as HT */
  sport?: string;
  /** Contest id — drives `pid`/`s5` key-value targeting on the over-logo GAM slot */
  contestId?: string | null;
}

/**
 * Square Results Modal
 * Shows results and potential winnings when clicking on selected square in results view
 */
export default function SquareResultsModal({
  isOpen,
  onClose,
  team1Name = 'DUKE',
  team1JerseyUrl,
  team2Name = 'ARIZ',
  team2JerseyUrl,
  team1Score,
  team2Score,
  sponsorLogoUrl,
  userCount,
  userPercentage,
  totalUsers,
  potentialWinnings,
  isCompleted = false,
  userSquareRowDigit,
  userSquareColDigit,
  sport,
  contestId,
}: SquareResultsModalProps) {
  const team1Image = useCachedImage(team1JerseyUrl);
  const team2Image = useCachedImage(team2JerseyUrl);
  const { isAdsEnabled, adPlacements, isPlacementEnabled } = useTenant();
  const isNBA = isNbaSquaresSport(sport);
  const team1HeaderPadding = squaresTeamAssetPadding(sport, team1JerseyUrl);
  const team2HeaderPadding = squaresTeamAssetPadding(sport, team2JerseyUrl);
  const team1ThumbPadding = squaresTeamAssetPadding(sport, team1JerseyUrl, 'thumb');
  const team2ThumbPadding = squaresTeamAssetPadding(sport, team2JerseyUrl, 'thumb');

  const isLogoAdEnabled =
    isAdsEnabled && adPlacements?.SQUARES_OVER_LOGO && isPlacementEnabled('SQUARES_OVER_LOGO');
  // Above the `isOpen` early return: hook order must not change between renders.
  const overLogoPlacement = useOverLogoPlacement(adPlacements?.SQUARES_OVER_LOGO, contestId);

  if (!isOpen) return null;

  const isValidUrl = (url: string | undefined | null): boolean => {
    return !!url && url.trim().length > 0;
  };

  const hasTeam1Jersey = isValidUrl(team1JerseyUrl) && !team1Image.errored;
  const hasTeam2Jersey = isValidUrl(team2JerseyUrl) && !team2Image.errored;

  // Use provided potentialWinnings or default to empty array (will show $0.00 for all periods)
  const winningsToDisplay =
    potentialWinnings && potentialWinnings.length > 0
      ? potentialWinnings
      : [
          { quarter: '1Q', amount: '$0.00' },
          { quarter: '2Q', amount: '$0.00' },
          { quarter: '3Q', amount: '$0.00' },
          { quarter: 'FT', amount: '$0.00' },
        ];

  // Use teamCodes mapping for 3-letter display (e.g., "Nuggets" → "DEN", "Nets" → "BKN")
  const team1Code = getTeamCode(team1Name ?? '');
  const team2Code = getTeamCode(team2Name ?? '');

  const parseAmount = (amount: string): number => {
    const n = Number(String(amount).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  // Total winnings across all periods (for display in modal)
  const totalWinningsAmount = winningsToDisplay.reduce((sum, w) => sum + parseAmount(w.amount), 0);
  const totalWinningsFormatted = `$${totalWinningsAmount.toFixed(2)}`;
  const hasAnyWinnings = totalWinningsAmount > 0;

  const hasUserSquareDigits =
    userSquareRowDigit !== undefined &&
    userSquareRowDigit !== null &&
    userSquareColDigit !== undefined &&
    userSquareColDigit !== null;

  return (
    <div data-test-id="square-results-modal" className={SQUARES_MODAL_OVERLAY_CLASS}>
      <SquaresModalBackdrop onClose={onClose} />

      {/* Modal Container. Not SquaresModalShell: the badge below is rendered outside
          the card and pulls it upwards with a negative margin, which the shell's
          close-button-then-card structure can't express. The shared backdrop, close
          button, gradient and geometry constants are reused directly instead. */}
      <div className={cn(SQUARES_MODAL_CONTAINER_CLASS, SQUARES_MODAL_SCROLL_CONTAINER_CLASS)}>
        {/* z-50 keeps the close button above the overlapping badge and clickable */}
        <SquaresModalCloseButton onClose={onClose} wrapperClassName="z-50 pointer-events-auto" />

        {/* Badge/Logo - in front of modal; center aligns with modal top so top half peeks over */}
        <div className="flex justify-center -mt-8 mb-0 z-30 relative">
          <div className="relative h-44 w-80 min-w-[260px] flex items-center justify-center">
            {/* Over Logo Ad Slot (1x1 tracking pixel, positioned just above logo) */}
            {isLogoAdEnabled && overLogoPlacement && (
              <div className="absolute bottom-full w-[1px] h-[1px] overflow-hidden pointer-events-none">
                <AdSlot
                  divId={generateAdDivId(adPlacements, 'SQUARES_OVER_LOGO', true, 2)}
                  adPlacement={overLogoPlacement}
                  placement="squares-over-logo"
                />
              </div>
            )}
            {sponsorLogoUrl && sponsorLogoUrl.trim().length > 0 ? (
              <img
                src={sponsorLogoUrl}
                alt="Sponsor Logo"
                className="h-full w-full object-contain"
              />
            ) : (
              <img
                src="/DFS_NFL_Wrangler_Squares_Logo_1080x1080%201.svg"
                alt="Squares Logo"
                className="h-full w-full object-contain"
              />
            )}
          </div>
        </div>

        {/* Modal Content - top edge at badge center; badge overlays from above */}
        <div
          className="rounded-xl overflow-y-auto shadow-2xl relative z-20 max-h-[85vh] -mt-[5.5rem] pt-2"
          style={{ background: SQUARES_MODAL_CARD_GRADIENT }}
        >
          {/* Score Display Section - top padding clears badge overlap; spacing between elements */}
          <div className="px-4 pt-10 pb-1">
            {/* Team Jerseys and Score - larger jerseys, tight gap to fit 340px */}
            <div className="flex items-center justify-center gap-1 -mb-3">
              {/* Team 1 — outer + square fit from squaresHeaderAssets (NBA portrait vs NCAA square) */}
              <div
                className="flex flex-shrink-0 items-center justify-center overflow-hidden"
                style={{
                  width: RESULTS_MODAL_HERO_OUTER_W_PX,
                  height: RESULTS_MODAL_HERO_OUTER_H_PX,
                }}
              >
                {hasTeam1Jersey ? (
                  <div
                    className="flex items-center justify-center overflow-hidden"
                    style={{
                      width: RESULTS_MODAL_HERO_SQUARE_FIT_PX,
                      height: RESULTS_MODAL_HERO_SQUARE_FIT_PX,
                    }}
                  >
                    {team1HeaderPadding ? (
                      <div
                        className="flex h-full w-full items-end justify-center box-border"
                        style={{ padding: team1HeaderPadding }}
                      >
                        <img
                          src={team1JerseyUrl!}
                          alt={`${team1Name} jersey`}
                          className="max-h-full max-w-full object-contain object-bottom"
                        />
                      </div>
                    ) : (
                      <img
                        src={team1JerseyUrl!}
                        alt={`${team1Name} jersey`}
                        className="max-h-full max-w-full object-contain"
                      />
                    )}
                  </div>
                ) : (
                  <div className="h-full w-full bg-zinc-700/30 border border-zinc-500/40 rounded" />
                )}
              </div>

              {/* Your Square numbers (row-col) - not game score */}
              <div className="flex flex-col items-center min-w-[44px] shrink-0">
                {userCount != null && (
                  <p
                    className="mb-0 text-center text-zinc-100"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                    }}
                  >
                    Your Square
                  </p>
                )}
                <span
                  className="text-zinc-100"
                  style={{
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 800,
                    fontSize: '36px',
                    lineHeight: '40px',
                  }}
                >
                  {hasUserSquareDigits ? `${userSquareRowDigit}-${userSquareColDigit}` : '—-—'}
                </span>
              </div>

              {/* Team 2 Jersey */}
              <div
                className="flex flex-shrink-0 items-center justify-center overflow-hidden"
                style={{
                  width: RESULTS_MODAL_HERO_OUTER_W_PX,
                  height: RESULTS_MODAL_HERO_OUTER_H_PX,
                }}
              >
                {hasTeam2Jersey ? (
                  <div
                    className="flex items-center justify-center overflow-hidden"
                    style={{
                      width: RESULTS_MODAL_HERO_SQUARE_FIT_PX,
                      height: RESULTS_MODAL_HERO_SQUARE_FIT_PX,
                    }}
                  >
                    {team2HeaderPadding ? (
                      <div
                        className="flex h-full w-full items-end justify-center box-border"
                        style={{ padding: team2HeaderPadding }}
                      >
                        <img
                          src={team2JerseyUrl!}
                          alt={`${team2Name} jersey`}
                          className="max-h-full max-w-full object-contain object-bottom"
                        />
                      </div>
                    ) : (
                      <img
                        src={team2JerseyUrl!}
                        alt={`${team2Name} jersey`}
                        className="max-h-full max-w-full object-contain"
                      />
                    )}
                  </div>
                ) : (
                  <div className="h-full w-full bg-zinc-700/30 border border-zinc-500/40 rounded" />
                )}
              </div>
            </div>

            {/* User Statistics Banner - full image 4.svg asset */}
            <div className="relative -mt-7 mb-0">
              <div className="relative w-full" style={{ aspectRatio: '292 / 76' }}>
                {/* image 4.svg - full asset visible (no cropping) */}
                <img
                  src="/image 4.svg"
                  alt=""
                  className="w-full h-full object-contain object-center"
                  aria-hidden="true"
                />
                {/* Text overlaid on top */}
                <p
                  className="absolute inset-0 flex items-center justify-center text-center px-4"
                  style={{
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 800,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0.5px',
                  }}
                >
                  <span style={{ color: '#53D337' }}>
                    {userCount.toLocaleString()} ({userPercentage.toFixed(2)}%)
                  </span>
                  <span style={{ color: '#F5F5F5' }}> OF {totalUsers.toLocaleString()} USERS</span>
                </p>
              </div>
            </div>

            {/* Winnings Section */}
            <div className="mb-1">
              <h3
                className="text-zinc-100 mb-1 text-center"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  fontWeight: 800,
                  fontSize: '18px',
                  lineHeight: '22px',
                  letterSpacing: '0.5px',
                }}
              >
                {isCompleted ? 'WINNINGS' : 'POTENTIAL WINNINGS'}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {winningsToDisplay.map((winning, idx) => {
                  const amountValue = parseAmount(winning.amount);
                  const isWin = amountValue > 0;
                  const hasPeriodScores =
                    winning.team1Score !== undefined && winning.team2Score !== undefined;
                  const isUnscored = !hasPeriodScores;
                  const pillColor = isUnscored ? '#C3C3C3' : isWin ? '#53D337' : '#FF3B58';
                  const pillTextColor = isUnscored || isWin ? '#000000' : '#FFFFFF';
                  const cardDelay = idx * 0.16;
                  const pillDelay = idx * 0.18;

                  return (
                    <div
                      key={idx}
                      className="rounded-lg py-2 px-4 animate-results-card-enter"
                      style={{
                        backgroundColor: '#262626',
                        boxShadow: '0px 4px 0px 0px #171717',
                        animationDelay: `${cardDelay}s`,
                        opacity: 0,
                      }}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span
                          className="text-zinc-100"
                          style={{
                            fontFamily: '"Saira Extra Condensed", sans-serif',
                            fontWeight: 800,
                            fontSize: '14px',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {formatResultsQuarterHeader(winning.quarter, isNBA)}
                        </span>
                        {/* Pill: grey with crown when unscored; amount + icon when scored - same pill shape for both */}
                        {isUnscored ? (
                          <span
                            className="relative rounded-full px-3 py-1 border border-white/20 inline-flex items-center justify-center gap-2 overflow-hidden"
                            style={{
                              backgroundColor: '#C3C3C3',
                              boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25) inset',
                              minWidth: 'max-content',
                            }}
                          >
                            <img
                              src="/DK-Crown-grey.png"
                              alt="Not yet scored"
                              className="w-4 h-4 object-contain"
                            />
                          </span>
                        ) : (
                          <span
                            className="relative rounded-full px-3 py-1 border border-white/20 inline-flex items-center justify-end gap-2 overflow-hidden"
                            style={{
                              backgroundColor: '#3A3A3A',
                              boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25) inset',
                              minWidth: 'max-content',
                            }}
                          >
                            <span
                              className="absolute inset-0 rounded-full origin-left"
                              style={{
                                backgroundColor: pillColor,
                                animation:
                                  'results-pill-reveal 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards',
                                animationDelay: `${pillDelay}s`,
                              }}
                            />
                            <span
                              className="relative z-10 inline-flex items-center gap-2"
                              style={{
                                color: pillTextColor,
                                fontFamily: '"Saira Extra Condensed", sans-serif',
                                fontWeight: 800,
                                fontSize: '16px',
                                lineHeight: '20px',
                                letterSpacing: '0.5px',
                              }}
                            >
                              <span>{winning.amount}</span>
                              <img
                                src={isWin ? '/circle-check.svg' : '/circle-highlight.svg'}
                                alt={isWin ? 'Winner' : 'Not a winner'}
                                className="w-4 h-4"
                              />
                            </span>
                          </span>
                        )}
                      </div>
                      {/* Center: graded scores with team jerseys, or crown logo when unscored */}
                      {hasPeriodScores ? (
                        <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-white/10">
                          {/* Team 1: jersey with 3-letter code underneath */}
                          <div className="flex flex-col items-center min-w-0 flex-1">
                            <div className="flex h-10 w-8 flex-shrink-0 items-center justify-center">
                              {hasTeam1Jersey ? (
                                <div
                                  className="flex items-center justify-center overflow-hidden"
                                  style={{
                                    width: RESULTS_MODAL_THUMB_SQUARE_FIT_PX,
                                    height: RESULTS_MODAL_THUMB_SQUARE_FIT_PX,
                                  }}
                                >
                                  {team1ThumbPadding ? (
                                    <div
                                      className="flex h-full w-full items-end justify-center box-border"
                                      style={{ padding: team1ThumbPadding }}
                                    >
                                      <img
                                        src={team1JerseyUrl!}
                                        alt=""
                                        className="max-h-full max-w-full object-contain object-bottom"
                                        aria-hidden
                                      />
                                    </div>
                                  ) : (
                                    <img
                                      src={team1JerseyUrl!}
                                      alt=""
                                      className="max-h-full max-w-full object-contain"
                                      aria-hidden
                                    />
                                  )}
                                </div>
                              ) : (
                                <div className="h-10 w-8 bg-zinc-700/40 rounded" />
                              )}
                            </div>
                            <span
                              className="text-zinc-200 text-center truncate w-full mt-0.5"
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: '10px',
                                lineHeight: '12px',
                              }}
                            >
                              {team1Code || team1Name}
                            </span>
                          </div>
                          {/* Center: bold score, quarter indicator below */}
                          <div className="flex flex-col items-center justify-center shrink-0 px-1">
                            <span
                              className="text-zinc-100"
                              style={{
                                fontFamily: '"Saira Extra Condensed", sans-serif',
                                fontWeight: 800,
                                fontSize: '18px',
                                lineHeight: '22px',
                                letterSpacing: '0.5px',
                              }}
                            >
                              {winning.team1Score}-{winning.team2Score}
                            </span>
                            <span
                              className="text-center mt-0.5"
                              style={{
                                color: '#A1A1AA',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 400,
                                fontSize: '10px',
                                lineHeight: '12px',
                              }}
                            >
                              {formatResultsQuarterSubLabel(winning.quarter, isNBA)}
                            </span>
                          </div>
                          {/* Team 2: jersey with 3-letter code underneath */}
                          <div className="flex flex-col items-center min-w-0 flex-1">
                            <div className="flex h-10 w-8 flex-shrink-0 items-center justify-center">
                              {hasTeam2Jersey ? (
                                <div
                                  className="flex items-center justify-center overflow-hidden"
                                  style={{
                                    width: RESULTS_MODAL_THUMB_SQUARE_FIT_PX,
                                    height: RESULTS_MODAL_THUMB_SQUARE_FIT_PX,
                                  }}
                                >
                                  {team2ThumbPadding ? (
                                    <div
                                      className="flex h-full w-full items-end justify-center box-border"
                                      style={{ padding: team2ThumbPadding }}
                                    >
                                      <img
                                        src={team2JerseyUrl!}
                                        alt=""
                                        className="max-h-full max-w-full object-contain object-bottom"
                                        aria-hidden
                                      />
                                    </div>
                                  ) : (
                                    <img
                                      src={team2JerseyUrl!}
                                      alt=""
                                      className="max-h-full max-w-full object-contain"
                                      aria-hidden
                                    />
                                  )}
                                </div>
                              ) : (
                                <div className="h-10 w-8 bg-zinc-700/40 rounded" />
                              )}
                            </div>
                            <span
                              className="text-zinc-200 text-center truncate w-full mt-0.5"
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: '10px',
                                lineHeight: '12px',
                              }}
                            >
                              {team2Code || team2Name}
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {/* Total winnings row */}
              <div
                className="rounded-lg py-2 px-4 mt-1 animate-results-card-enter flex items-center justify-between"
                style={{
                  backgroundColor: '#262626',
                  boxShadow: '0px 4px 0px 0px #171717',
                  animationDelay: `${winningsToDisplay.length * 0.16}s`,
                  opacity: 0,
                }}
              >
                <span
                  className="text-zinc-100"
                  style={{
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 800,
                    fontSize: '16px',
                    letterSpacing: '0.5px',
                  }}
                >
                  Total
                </span>
                <span
                  className="inline-flex items-center gap-2"
                  style={{
                    color: hasAnyWinnings ? '#53D337' : '#A1A1AA',
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 800,
                    fontSize: '18px',
                    lineHeight: '22px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {totalWinningsFormatted}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
